import React, { useState } from 'react';
import { ArrowUpRightIcon, CheckCircle2Icon, FacebookIcon, InstagramIcon, MailIcon, MapPinIcon, MusicIcon, PhoneIcon, YoutubeIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionHeading } from '../components/SectionHeading';
import { CLUB } from '../data/site';
import { DOJO } from '../data/practical';
type Status = 'idle' | 'sending' | 'sent' | 'error';
type Errors = Partial<Record<'name' | 'email' | 'message', string>>;
const SUBJECTS = ['Demande d’information', 'Cours d’essai', 'Inscription', 'Partenariat / mécénat', 'Presse', 'Autre'];
const SOCIAL_LINKS = [{
  label: 'Facebook',
  href: 'https://facebook.com',
  Icon: FacebookIcon
}, {
  label: 'Instagram',
  href: 'https://instagram.com',
  Icon: InstagramIcon
}, {
  label: 'TikTok',
  href: 'https://tiktok.com',
  Icon: MusicIcon
}, {
  label: 'YouTube',
  href: 'https://youtube.com',
  Icon: YoutubeIcon
}];
const fieldClass = 'zk-focus mt-2 w-full border border-white/15 bg-ink px-4 py-3 text-sm text-bone placeholder:text-muted/60 focus:border-gold';
export function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Errors>({});
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: SUBJECTS[0],
    message: ''
  });
  function update(field: keyof typeof form, value: string) {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  }
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Errors = {};
    if (!form.name.trim()) nextErrors.name = 'Merci d’indiquer votre nom.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = 'Merci d’indiquer une adresse email valide.';
    if (form.message.trim().length < 10) nextErrors.message = 'Votre message doit contenir au moins 10 caractères.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 700);
  }
  return <>
      <PageHeader kanji="連絡" eyebrow="Contact" title="Écrivez-nous, passez au dojo" intro="Une question sur les cours, une demande d’inscription ou un projet de partenariat : nous répondons sous 48 heures." data-id="element-184" data-mp="1h78x5tdbkusb" />

      <section className="mx-auto grid max-w-shell gap-12 px-5 py-16 lg:grid-cols-[1.2fr_1fr] lg:px-8 lg:py-20" data-id="element-185" data-mp="1r4lt3c7zrfg3">
        <div data-id="element-186" data-mp="yi14gjiwhhg8">
          <SectionHeading kanji="伝言" title="Formulaire de contact" data-id="element-187" data-mp="yiaug73y3olx" />

          {status === 'sent' ? <div role="status" className="mt-10 flex items-start gap-4 border border-gold/40 bg-panel p-8" data-id="element-188" data-mp="1d6x9s8tkogd5">
              <CheckCircle2Icon size={22} className="mt-0.5 shrink-0 text-gold" aria-hidden data-id="element-189" data-mp="18dvtbwdpw8rg" />
              <div data-id="element-190" data-mp="ko9u58xchtjg">
                <p className="font-display text-lg text-bone" data-id="element-191" data-mp="frb34z8za2ph">Message envoyé</p>
                <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-192" data-mp="fp6yug7631oo">
                  Merci {form.name.split(' ')[0]}, votre message a bien été transmis à {CLUB.email}. Nous revenons vers
                  vous sous 48 heures.
                </p>
                <button type="button" onClick={() => {
              setForm({
                name: '',
                email: '',
                phone: '',
                subject: SUBJECTS[0],
                message: ''
              });
              setStatus('idle');
            }} className="zk-focus mt-5 font-display text-sm uppercase tracking-[0.14em] text-gold hover:text-gold-soft" data-id="element-193" data-mp="2hh8k68v79o2t">
                  Envoyer un autre message
                </button>
              </div>
            </div> : <form onSubmit={handleSubmit} noValidate className="mt-10 space-y-6" data-id="element-194" data-mp="35502pu7c2xcj">
              <div className="grid gap-6 sm:grid-cols-2" data-id="element-195" data-mp="1pp1cvz1l3o13">
                <div data-id="element-196" data-mp="1prnl80wau7kd">
                  <label htmlFor="name" className="font-display text-xs uppercase tracking-[0.16em] text-muted" data-id="element-197" data-mp="1tqmo7fygzu96">
                    Nom et prénom *
                  </label>
                  <input id="name" name="name" value={form.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className={fieldClass} data-id="element-198" data-mp="29zu4zsgkka0u" />
                  {errors.name && <p id="name-error" className="mt-2 text-xs text-crimson" data-id="element-199" data-mp="2nxacl7alq9u6">
                      {errors.name}
                    </p>}
                </div>
                <div data-id="element-200" data-mp="2ex42lve43rzr">
                  <label htmlFor="email" className="font-display text-xs uppercase tracking-[0.16em] text-muted" data-id="element-201" data-mp="2exnaskdyn20l">
                    Email *
                  </label>
                  <input id="email" name="email" type="email" value={form.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} className={fieldClass} data-id="element-202" data-mp="17h7g0tg3lst8" />
                  {errors.email && <p id="email-error" className="mt-2 text-xs text-crimson" data-id="element-203" data-mp="3sy9u6le4p940">
                      {errors.email}
                    </p>}
                </div>
                <div data-id="element-204" data-mp="6ofreca1833h">
                  <label htmlFor="phone" className="font-display text-xs uppercase tracking-[0.16em] text-muted" data-id="element-205" data-mp="6nnsarh87n8g">
                    Téléphone
                  </label>
                  <input id="phone" name="phone" type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} className={fieldClass} data-id="element-206" data-mp="fp43h1fv7tdq" />
                </div>
                <div data-id="element-207" data-mp="3kt8vmegxwkxh">
                  <label htmlFor="subject" className="font-display text-xs uppercase tracking-[0.16em] text-muted" data-id="element-208" data-mp="3kspkbglsousf">
                    Objet
                  </label>
                  <select id="subject" name="subject" value={form.subject} onChange={e => update('subject', e.target.value)} className={fieldClass} data-id="element-209" data-mp="3tu7356ql7akt">
                    {SUBJECTS.map(subject => <option key={subject} value={subject} data-id="element-210" data-mp="2mkpcla5lrq8">
                        {subject}
                      </option>)}
                  </select>
                </div>
              </div>

              <div data-id="element-211" data-mp="ceqqolmapli3">
                <label htmlFor="message" className="font-display text-xs uppercase tracking-[0.16em] text-muted" data-id="element-212" data-mp="qcf458e19qd4">
                  Votre message *
                </label>
                <textarea id="message" name="message" rows={6} value={form.message} onChange={e => update('message', e.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'message-error' : undefined} className={fieldClass} data-id="element-213" data-mp="lb7u12of4f3a" />
                {errors.message && <p id="message-error" className="mt-2 text-xs text-crimson" data-id="element-214" data-mp="m7l79m8rnnqg">
                    {errors.message}
                  </p>}
              </div>

              <p className="text-xs leading-relaxed text-muted" data-id="element-215" data-mp="cayp557tras4">
                Les informations transmises sont utilisées uniquement pour répondre à votre demande et ne sont jamais
                cédées à des tiers.
              </p>

              <button type="submit" disabled={status === 'sending'} className="zk-focus inline-flex items-center gap-2 bg-gold px-7 py-3 font-display text-sm font-semibold uppercase tracking-[0.14em] text-ink transition-colors duration-150 ease-out hover:bg-gold-soft disabled:cursor-not-allowed disabled:opacity-60" data-id="element-216" data-mp="3aabibmcfcfr">
                {status === 'sending' ? 'Envoi en cours…' : 'Envoyer le message'}
              </button>
            </form>}
        </div>

        <aside className="space-y-10" data-id="element-217" data-mp="8vyiaez8le36">
          <div className="border border-gold/20 p-7" data-id="element-218" data-mp="4s8vnv24h6ks">
            <h2 className="font-display text-lg uppercase tracking-[0.08em] text-bone" data-id="element-219" data-mp="4u3ft75kkunt">Coordonnées</h2>
            <address className="mt-5 space-y-4 not-italic text-sm text-muted" data-id="element-220" data-mp="3w4ttidsfqn39">
              <p className="flex gap-3" data-id="element-221" data-mp="3r7v1c0sc0q7p">
                <MapPinIcon size={16} className="mt-0.5 shrink-0 text-gold" aria-hidden data-id="element-222" data-mp="3m8qsxcd8ewif" />
                <span data-id="element-223" data-mp="3h9e3qkyw1dco">
                  <span className="block text-bone" data-id="element-224" data-mp="3h9nkrj1yga68">{DOJO.name}</span>
                  {DOJO.address}
                </span>
              </p>
              <p className="flex items-center gap-3" data-id="element-225" data-mp="3d8luhrq0n8ge">
                <PhoneIcon size={16} className="shrink-0 text-gold" aria-hidden data-id="element-226" data-mp="1sqjev0er7bd8" />
                <a href={CLUB.phoneHref} className="zk-focus hover:text-bone" data-id="element-227" data-mp="1wr3d67b2cl0y">
                  {CLUB.phone}
                </a>
              </p>
              <p className="flex items-center gap-3" data-id="element-228" data-mp="21qzh66jrgcgi">
                <MailIcon size={16} className="shrink-0 text-gold" aria-hidden data-id="element-229" data-mp="26patcnxj2z1w" />
                <a href={`mailto:${CLUB.email}`} className="zk-focus break-all hover:text-bone" data-id="element-230" data-mp="191sj18o7xovs">
                  {CLUB.email}
                </a>
              </p>
            </address>
            <a href={DOJO.mapHref} target="_blank" rel="noreferrer" className="zk-focus mt-6 inline-flex items-center gap-2 font-display text-sm uppercase tracking-[0.14em] text-gold hover:text-gold-soft" data-id="element-231" data-mp="1dysrfq8jj3q2">
              Voir le plan d’accès
              <ArrowUpRightIcon size={16} aria-hidden data-id="element-232" data-mp="q9gfiiltwhk2" />
            </a>
          </div>

          <div className="border border-white/10 bg-panel p-7" data-id="element-233" data-mp="v8aybkjsq646">
            <h2 className="font-display text-lg uppercase tracking-[0.08em] text-bone" data-id="element-234" data-mp="owsqm0ow6r5s">Réseaux sociaux</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-235" data-mp="tv418dn0h5p2">
              Photos d’entraînement, résultats et informations de dernière minute : suivez la vie du dojo au quotidien.
            </p>
            <ul className="mt-6 space-y-3" data-id="element-236" data-mp="13ucuf4a1ygad">
              {SOCIAL_LINKS.map(({
              label,
              href,
              Icon
            }) => <li key={label} data-id="element-237" data-mp="68xj2yjvi9l4">
                  <a href={href} target="_blank" rel="noreferrer" className="zk-focus flex items-center gap-3 text-sm text-bone transition-colors duration-150 ease-out hover:text-gold" data-id="element-238" data-mp="b5xrhg473ofe">
                    <Icon size={18} className="text-gold" aria-hidden data-id="element-239" data-mp="kxtt315hpwod" />
                    {label}
                    <ArrowUpRightIcon size={14} className="ml-auto text-muted" aria-hidden data-id="element-240" data-mp="3jk6abwgx10ud" />
                  </a>
                </li>)}
            </ul>
          </div>
        </aside>
      </section>
    </>;
}